import { makeAutoObservable, toJS } from 'mobx';
import { matchPath } from 'react-router-dom';
import history from '../../history';
import { STATUS_TYPE } from '../../global/constants';
import { rootStore } from '../rootStore';
import { ROUTES } from '../../routes/urls';
import { getProjectIdFromPath } from '../../routes/routeHelpers';

export type Tab = {
  id: number;
  label: string;
  icon: string;
  path: string;
  iconProps: {
    width?: string;
    height?: string;
    className?: string;
    stroke?: string;
    viewBox?: string;
  };
};

export const DEFAULT_PATH = '/';
export const DEFAULT_TAB_ID = 1;
export const MAX_TABS = 10;

export class TabsStore {
  tabs: Tab[];
  selectedTabId: number = DEFAULT_TAB_ID;
  nextTabId: number = DEFAULT_TAB_ID + 1;

  constructor() {
    makeAutoObservable(this);

    const { location } = history;
    const path = location.pathname + location.search;
    const { icon, label, iconProps } = getIconAndLabel(path);
    this.tabs = [{ id: DEFAULT_TAB_ID, label, icon, path, iconProps }];
  }

  addTab() {
    if (this.tabs.length >= MAX_TABS) return;

    const { icon, label, iconProps } = getIconAndLabel(DEFAULT_PATH);

    const newTab: Tab = {
      id: this.nextTabId,
      label,
      icon,
      iconProps,
      path: DEFAULT_PATH
    };

    const currentTab = this.tabs.find((tab) => tab.id === this.selectedTabId);
    if (currentTab) {
      history.replace({ state: toJS(currentTab) });
    }

    this.tabs = [...this.tabs, newTab];
    this.selectedTabId = this.nextTabId;
    this.nextTabId = this.nextTabId + 1;

    history.push(DEFAULT_PATH, toJS(newTab));
  }

  removeTab(tabId: number) {
    if (this.tabs.length <= 1) return;
    const cloneTabs = this.tabs;
    const newTabs = this.tabs.filter((tab) => tab.id !== tabId);
    this.tabs = newTabs;

    if (tabId === this.selectedTabId) {
      // If current tab was closed then determine the next active tab
      let newSelectedTab: Tab;
      const deletedTabIndex = cloneTabs.findIndex((tab) => tab.id === tabId);
      if (deletedTabIndex === 0) {
        // if first tab was closed then select the first tab in new tabs list
        newSelectedTab = newTabs[0];
      } else if (deletedTabIndex === cloneTabs.length - 1) {
        // if last tab was closed then select the last tab in new tabs list
        newSelectedTab = newTabs[newTabs.length - 1];
      } else {
        // select the next tab
        newSelectedTab = newTabs[deletedTabIndex];
      }
      if (newSelectedTab) {
        this.selectedTabId = newSelectedTab?.id;
        history.push(newSelectedTab.path, newSelectedTab);
      } else {
        history.push(ROUTES.INBOX);
      }
    }
  }

  handleTabClick(tab: Tab, onChangeMessageGroup: (groupId: string | null) => Promise<void>) {
    if (tab.id === this.selectedTabId) return;

    const currentTab = this.tabs.find((tab) => tab.id === this.selectedTabId);
    if (currentTab) {
      history.replace({ state: toJS(currentTab) });
    }
    const projectId = getProjectIdFromPath(tab?.path);
    if (projectId) {
      rootStore.projectStore.setCurrentProject(projectId);
    }
    if (currentTab?.path.startsWith('/messages')) {
      const params = new URLSearchParams(tab.path?.split('?')[1]);
      const chatGroupId = params.get('chatGroup');
      onChangeMessageGroup(chatGroupId);
    }
    this.selectedTabId = tab.id;
    history.push(tab.path, toJS(tab));
  }

  onLocationChange() {
    const { location } = history;
    let selectedTab = this.selectedTabId;

    const tab = location.state as Tab | null;
    if (tab && tab.id !== this.selectedTabId && this.tabs.some((x) => x.id === tab.id)) {
      this.selectedTabId = tab.id;
      selectedTab = tab.id;
    }

    const path = location.pathname + location.search;

    const selectedTabIndex = this.tabs.findIndex((tab) => tab.id === selectedTab);
    if (selectedTabIndex !== -1) {
      const { icon, label, iconProps } = getIconAndLabel(path);

      this.tabs[selectedTabIndex].icon = icon;
      this.tabs[selectedTabIndex].label = label;
      this.tabs[selectedTabIndex].path = path;
      this.tabs[selectedTabIndex].iconProps = iconProps;
    }
  }
}

const getIconAndLabel = (path: string) => {
  const { projectStore, taskStore, workspaceStore, chatMessageStore } = rootStore;
  const { currentProject } = projectStore;
  const { currentTask } = taskStore;
  const { currentWorkspace } = workspaceStore;
  const { currentChatGroup } = chatMessageStore || {};

  // Pick the task/project from the tab's own URL so a tab always shows its own title,
  // not the globally "current" one (which lags behind during a tab switch).
  const pathname = path.split('?')[0];
  const taskId = matchPath<{ taskId: string }>(pathname, { path: ROUTES.TASK_DETAILS })?.params?.taskId;
  const tabTask = (taskId && taskStore.tasksById.get(taskId)) || currentTask;

  const projectId = getProjectIdFromPath(pathname);
  const tabProject = (projectId && projectStore.projectsById.get(projectId)) || currentProject;

  const paths: Record<string, string> = {
    '/my-tasks': 'my-task',
    '/invoices': 'invoice',
    '/timeTracking': 'time-tracking',
    '/report': 'report',
    '/projects': 'project',
    '/messages': 'messages',
    '/customer': 'customer',
    '/settings': 'settings',
    '/members': 'members',
    '/views': 'views',
    '/': 'inbox'
  };

  const matchedKey = Object.keys(paths).find((key) => path.startsWith(key));

  const defaultIconAndLabel = { icon: '', label: 'Unknown', iconProps: {} };
  if (!matchedKey) {
    return defaultIconAndLabel;
  }

  switch (paths[matchedKey]) {
    case 'my-task':
      return { icon: 'navbar-MyTask-icon', label: 'My Tasks', iconProps: {} };
    case 'invoice':
      return { icon: 'invoice-icon', label: 'Invoices', iconProps: {} };
    case 'time-tracking':
      return { icon: 'navbar-TimeTracking-icon', label: 'Time Tracking', iconProps: {} };
    case 'report':
      return { icon: 'navbar-Report-icon', label: 'Reports', iconProps: {} };
    case 'project': {
      if (path.includes('/tasks/details')) {
        if (tabTask?.project?.statusEnable) {
          const defaultStatus = tabTask?.project?.statusData.find((status) => status.Default);
          const status =
            tabTask?.project?.statusData.find((status) => status['_id'] === tabTask?.statusId) || defaultStatus;
          switch (status?.Type ?? STATUS_TYPE.UNSTARTED) {
            case STATUS_TYPE.BACKLOG:
              return {
                icon: 'task-status-icon',
                label: tabTask?.name || 'Task',
                iconProps: { viewBox: '0 0 16 16' }
              };
            case STATUS_TYPE.CANCELLED:
              return {
                icon: 'task-cancelled-icon',
                label: tabTask?.name || 'Task',
                iconProps: { viewBox: '0 0 16 16' }
              };
            case STATUS_TYPE.COMPLETED:
              return {
                icon: 'task-completed-icon',
                label: tabTask?.name || 'Task',
                iconProps: { viewBox: '0 0 16 16' }
              };
            case STATUS_TYPE.IN_PROGRESS:
              return {
                icon: 'task-inprogress-icon',
                label: tabTask?.name || 'Task',
                iconProps: { viewBox: '0 0 16 16' }
              };
            case STATUS_TYPE.IN_REVIEW:
              return {
                icon: 'task-inreview-icon',
                label: tabTask?.name || 'Task',
                iconProps: { viewBox: '0 0 16 16' }
              };
            case STATUS_TYPE.UNSTARTED: // White background
              return {
                icon: 'task-todo-icon',
                label: tabTask?.name || 'Task',
                iconProps: { className: 'task-todo-icon', viewBox: '0 0 16 16' }
              };
            default:
              return {
                icon: 'task-todo-icon',
                label: tabTask?.name || 'Task',
                iconProps: { className: 'task-todo-icon', viewBox: '0 0 16 16' }
              };
          }
        } else {
          if (tabTask?.status) {
            return {
              icon: 'task-completed-icon',
              label: tabTask?.name || 'Task',
              iconProps: { viewBox: '0 0 16 16' }
            };
          } else {
            // Check mark white
            return {
              icon: 'open-icon',
              label: tabTask?.name || 'Task',
              iconProps: { className: 'task-open-icon', viewBox: '0 0 16 16' }
            };
          }
        }
      }
      return {
        icon: 'project-icon',
        label: tabProject?.name || 'Project',
        iconProps: {}
      };
    }
    case 'messages':
      return { icon: 'message-icon', label: currentChatGroup?.name || 'Messages', iconProps: {} };
    case 'customer':
      return { icon: 'customer-icon', label: 'Customers', iconProps: {} };
    case 'settings':
      return { icon: 'settings-icon', label: 'Settings', iconProps: {} };
    case 'inbox':
      return { icon: 'inbox-icon', label: 'Inbox', iconProps: {} };
    case 'members':
      const match = path.match(/^\/members\/([^/]+)(?:\/|$)/);
      const memberId = match ? match[1] : null;
      const memberDetails = currentWorkspace?.users?.find((user) => user.id === memberId);
      return { icon: 'user-icon', label: memberDetails?.name || 'Unkown', iconProps: {} };
    case 'views':
      return { icon: 'navbar-views-icon', label: 'Views', iconProps: {} };
    default:
      return defaultIconAndLabel;
  }
};
